import type {AvmSpecSegment} from "@reva/shared/reva-spec";
import {resolveExposeSegment} from "@reva/shared/segment-description";

/**
 * #19159: Herkunft der Exposé-Schicht — woher die Werte stammen und wann sie eingelesen wurden.
 *
 * Die Angabe reist als reservierter Schlüssel IN der Exposé-Schicht mit, weil diese serverseitig ein
 * undurchsichtiger JSON-Block ist: kein neues Elasticsearch-Feld, und Snapshots übernehmen sie von selbst.
 * Der Schlüssel ist bewusst kein Parametername, deshalb ignorieren ihn Ausgabe-Grid und Parameterauflösung.
 */
export const IMMODROP_SOURCE_KEY = "_importSource";

export interface ImmodropSource {
    /** gescannte Exposé-URL, wenn der Import über eine URL lief */
    url?: string;
    /** Dateiname des hochgeladenen PDFs, wenn der Import über einen Upload lief */
    filename?: string;
    /** Id des in der Orders-API abgelegten PDFs, sofern der Upload dorthin geklappt hat */
    documentId?: string;
    /** Segment, das die Extraktion geliefert hat — auch eines, das REVA nicht führt (s. readExposeSegment) */
    segment?: string;
    /** Zeitpunkt des Imports, ISO-8601 */
    importedAt: string;
}

/**
 * Baut die Herkunftsangabe für einen gerade gelaufenen Import.
 * @param url gescannte URL oder null
 * @param filename hochgeladener Dateiname oder null
 * @param documentId Id des in der Orders-API abgelegten PDFs oder null
 * @param segment Segment aus der Extraktion oder null
 * @param importedAt Zeitpunkt (Default: jetzt)
 * @return die Herkunftsangabe
 */
export function buildImmodropSource(
    url: string | null | undefined,
    filename: string | null | undefined,
    documentId: string | null | undefined,
    segment: string | null | undefined,
    importedAt: Date = new Date(),
): ImmodropSource {
    return {
        ...(url ? {url} : {}),
        ...(filename ? {filename} : {}),
        ...(documentId ? {documentId} : {}),
        ...(segment ? {segment} : {}),
        importedAt: importedAt.toISOString(),
    };
}

/**
 * Liest die Herkunftsangabe aus den Parametern der Exposé-Schicht.
 * @param params die Parameter der Schicht (oder null)
 * @return die Herkunft, oder null wenn keine brauchbare hinterlegt ist
 */
export function readImmodropSource(params: object | null | undefined): ImmodropSource | null {
    const raw = (params as Record<string, unknown> | null | undefined)?.[IMMODROP_SOURCE_KEY];
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
    const {url, filename, documentId, segment, importedAt} = raw as Record<string, unknown>;
    if (typeof importedAt !== "string" || !importedAt) return null;
    return {
        ...(typeof url === "string" && url ? {url} : {}),
        ...(typeof filename === "string" && filename ? {filename} : {}),
        ...(typeof documentId === "string" && documentId ? {documentId} : {}),
        ...(typeof segment === "string" && segment ? {segment} : {}),
        importedAt,
    };
}

/**
 * Sind außer der Herkunftsangabe überhaupt Werte in der Schicht?
 * @param params die Parameter der Schicht (oder null)
 * @return true, wenn mindestens ein echter Parameter enthalten ist
 */
export function hasImmodropValues(params: object | null | undefined): boolean {
    return Object.keys(params ?? {}).some((key) => key !== IMMODROP_SOURCE_KEY);
}

/**
 * Schlüssel, die die Extraktion neben den eigentlichen Werten mitliefert: Treffergüte und Fundstelle je
 * Parameter sowie Angaben zum Auswertungslauf. Sie sind keine Objektparameter und gehören in keine Bilanz.
 */
const TECHNICAL_KEY_SUFFIXES = ["_confidence", "_evidence"];
const TECHNICAL_KEYS = new Set(["ai_extraction_info", IMMODROP_SOURCE_KEY]);

/**
 * Adress- und Angebotsangaben. Beide werden bewusst nie in die Bewertung übernommen — die Adresse, weil
 * sie der Bewertung gehört und nicht dem Exposé (s. TabObjectImmodrop), das Angebot, weil es den
 * Fundzeitpunkt der Anzeige beschreibt und nicht das Objekt. Als "ignoriert" zu melden wäre Rauschen.
 */
const NEVER_IMPORTED_PREFIXES = ["addr_", "offer_"];
const NEVER_IMPORTED_KEYS = new Set(["address", "code_postal"]);

function isTechnicalKey(key: string): boolean {
    return TECHNICAL_KEYS.has(key) || TECHNICAL_KEY_SUFFIXES.some((suffix) => key.endsWith(suffix));
}

function isNeverImportedKey(key: string): boolean {
    return NEVER_IMPORTED_KEYS.has(key) || NEVER_IMPORTED_PREFIXES.some((prefix) => key.startsWith(prefix));
}

/** Bilanz eines Exposé-Imports, für die Herkunftszeile im Exposé-Tab (#19159). */
export interface ImmodropImportSummary {
    /** Anzahl der Werte, die als Objektparameter in der Bewertung ankommen */
    importedCount: number;
    /** inhaltliche Werte, die der AVM-Spec für dieses Segment unbekannt sind — alphabetisch */
    ignored: string[];
}

/**
 * Zählt, was vom Exposé tatsächlich als Objektparameter ankommt, und benennt, was liegen blieb.
 *
 * Die Exposé-Schicht bekommt beim Import den kompletten Auswertungs-JSON — also auch Schlüssel, die die
 * AVM-Spec des Segments gar nicht kennt. Die tauchen im Ausgabe-Grid nie auf, und ohne Hinweis wirkt es,
 * als wäre das Exposé vollständig übernommen worden.
 * @param params die Parameter der Exposé-Schicht
 * @param specParams die Eingabeparameter der AVM-Spec für das Segment (param → Definition)
 * @return Anzahl übernommener Werte und die Namen der ignorierten
 */
export function summarizeImmodropImport(
    params: object | null | undefined,
    specParams: object | null | undefined,
): ImmodropImportSummary {
    const known = new Set(Object.keys(specParams ?? {}));
    const contentKeys = Object.keys(params ?? {}).filter((key) => !isTechnicalKey(key) && !isNeverImportedKey(key));
    const imported = contentKeys.filter((key) => known.has(key));
    const ignored = contentKeys.filter((key) => !known.has(key)).sort();
    return {importedCount: imported.length, ignored};
}

const KNOWN_PORTALS: [domain: string, name: string][] = [
    ["immobilienscout24.de", "ImmoScout24"],
    ["immowelt.de", "Immowelt"],
    ["immonet.de", "Immonet"],
    ["kleinanzeigen.de", "Kleinanzeigen"],
];

/**
 * Anzeigename des Portals hinter einer Exposé-URL: bekannte Börsen mit ihrem Namen, sonst der Host ohne "www.".
 * @param url die gescannte Exposé-URL
 * @return der Name, oder null wenn die URL keine http(s)-Adresse ist — dann darf sie auch nicht verlinkt werden
 */
export function exposePortalName(url: string): string | null {
    let parsed: URL;
    try {
        parsed = new URL(url);
    } catch {
        return null;
    }
    if (parsed.protocol !== "https:" && parsed.protocol !== "http:") return null;
    const host = parsed.hostname.replace(/^www\./, "");
    return KNOWN_PORTALS.find(([domain]) => host === domain || host.endsWith(`.${domain}`))?.[1] ?? host;
}

/**
 * Segment laut Exposé, sofern REVA es führt. Ohne gespeichertes Segment (Extraktion lieferte keins, oder
 * Import aus der Zeit davor) wird es aus dem differenzierten Objekttyp der Schicht abgeleitet.
 * @param params die Parameter der Exposé-Schicht (oder null)
 * @return das Segment, oder null wenn keines bestimmbar ist
 */
export function readExposeSegment(params: object | null | undefined): AvmSpecSegment | null {
    const buildingType = (params as Record<string, unknown> | null | undefined)?.building_type;
    return resolveExposeSegment(readImmodropSource(params)?.segment, buildingType);
}
